import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import Dashboard from "@/pages/Dashboard";
import BrowseJobs from "@/pages/BrowseJobs";
import JobDetail from "@/pages/JobDetail";
import CreateJob from "@/pages/CreateJob";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import EditStudioProfile from "@/pages/EditStudioProfile";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import CreateJobPage from "@/pages/CreateJobPage";
import JobCandidatesPage from "@/pages/JobCandidatesPage";
import AdminPage from "@/pages/AdminPage";
import MessagesPage from "@/pages/MessagesPage";
import ReviewRequestPage from "@/pages/ReviewRequestPage";
import ContractSignPage from "@/pages/ContractSignPage";
import ContractsPage from "@/pages/ContractsPage";
import HireConfirmationPage from "@/pages/HireConfirmationPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import MyJobsPage from "@/pages/MyJobsPage";
import MyReviewsPage from "@/pages/MyReviewsPage";
import HelpPage from "@/pages/HelpPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ContactPage from "@/pages/ContactPage";
import PhotographerOfTheWeekPage from "@/pages/PhotographerOfTheWeekPage";
import AccessibilityPage from "@/pages/AccessibilityPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForStudiosPage from "@/pages/ForStudiosPage";
import ForSecondShootersPage from "@/pages/ForSecondShootersPage";
import ArticlesListPage from "@/pages/ArticlesListPage";
import ArticlePage from "@/pages/ArticlePage";

// In production (single-service deployment), use relative URL
// In development, use REACT_APP_BACKEND_URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/for-studios" element={<ForStudiosPage />} />
      <Route path="/for-second-shooters" element={<ForSecondShootersPage />} />
      <Route path="/articles" element={<ArticlesListPage />} />
      <Route path="/articles/:slug" element={<ArticlePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/photographer-of-the-week" element={<PhotographerOfTheWeekPage />} />
      <Route path="/photographer-of-the-week/:kind" element={<PhotographerOfTheWeekPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot-password/reset" element={<ForgotPasswordPage />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><BrowseJobs /></ProtectedRoute>} />
      <Route path="/jobs/create" element={<ProtectedRoute><CreateJobPage /></ProtectedRoute>} />
      <Route path="/jobs/my-jobs" element={<ProtectedRoute><MyJobsPage /></ProtectedRoute>} />
      <Route path="/my-reviews" element={<ProtectedRoute><MyReviewsPage /></ProtectedRoute>} />
      <Route path="/jobs/browse" element={<ProtectedRoute><BrowseJobs /></ProtectedRoute>} />
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
      <Route path="/jobs/:jobId/candidates" element={<ProtectedRoute><JobCandidatesPage /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
      <Route path="/profile/edit/studio" element={<ProtectedRoute><EditStudioProfile /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/messages/:threadId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
      <Route path="/contracts/:contractId" element={<ProtectedRoute><ContractSignPage /></ProtectedRoute>} />
      <Route path="/hiring/confirm/:hireId" element={<ProtectedRoute><HireConfirmationPage /></ProtectedRoute>} />
      <Route path="/password-reset" element={<ProtectedRoute><PasswordResetPage /></ProtectedRoute>} />
      <Route path="/notifications/settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
      <Route path="/review/:requestId" element={<ReviewRequestPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem("token");
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    // Always fetch fresh user data from backend
    try {
      const response = await fetch(`${API}/users/me`, {
        headers: { "Authorization": `Bearer ${storedToken}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(storedToken);
        localStorage.setItem("user", JSON.stringify(userData));
      } else {
        // Token invalid, clear storage
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      // On network error, use cached data if available
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      }
    }

    setLoading(false);
  };

  const login = async (email, password) => {
    let response;
    try {
      response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
    } catch (e) {
      throw new Error("Network error. Please check your connection and try again.");
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      // If we can't parse JSON but got a response, check the status
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Invalid email or password. Please try again.");
        }
        throw new Error("Login failed. Please try again.");
      }
      throw new Error("Unexpected server response. Please try again.");
    }
    
    if (!response.ok) {
      // Show specific error message from backend when available
      if (response.status === 401) {
        throw new Error(data.detail || "Invalid email or password. Please try again.");
      }
      throw new Error(data.detail || "Login failed. Please try again.");
    }

    // Password was right but a 2FA code is still owed — LoginPage asks for it
    if (data.two_factor_required) {
      return data;
    }

    setUser(data.user);
    setToken(data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("token", data.token);
    return data;
  };

  const verifyTwoFactor = async (pendingToken, code) => {
    let response;
    try {
      response = await fetch(`${API}/auth/2fa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pending_token: pendingToken, code })
      });
    } catch (e) {
      throw new Error("Network error. Please check your connection and try again.");
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.detail || "That code didn't work. Please try again.");
      error.status = response.status;
      throw error;
    }

    setUser(data.user);
    setToken(data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    localStorage.setItem("token", data.token);
    return data;
  };

  const register = async (name, email, password, roles, city, state) => {
    // Use XMLHttpRequest to avoid fetch interceptor issues
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/auth/register`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      
      xhr.onload = function() {
        let data = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch (e) {
          // JSON parsing failed
        }
        
        if (xhr.status >= 200 && xhr.status < 300) {
          setUser(data.user);
          setToken(data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("token", data.token);
          resolve(data);
        } else {
          const errorDetail = data.detail || "";
          
          if (errorDetail.toLowerCase().includes("already registered") || 
              errorDetail.toLowerCase().includes("already exists")) {
            reject(new Error("This email is already registered. Please sign in instead, or use a different email address."));
          } else {
            reject(new Error(errorDetail || "Registration failed. Please try again."));
          }
        }
      };
      
      xhr.onerror = function() {
        reject(new Error("Network error. Please check your connection and try again."));
      };
      
      xhr.send(JSON.stringify({ name, email, password, roles, city: city || "", state: state || "" }));
    });
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyTwoFactor, register, logout, updateUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
}

import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import IdleTimeoutManager from "@/components/IdleTimeoutManager";

function AppContent() {
  const { token, logout, setToken, updateUser } = useAuth();
  const handleTokenRefresh = (newToken, newUser) => {
    if (setToken) setToken(newToken);
    if (updateUser && newUser) updateUser(newUser);
  };
  return (
    <>
      <AppRouter />
      <CookieConsentBanner />
      <Toaster position="top-right" />
      {token && <IdleTimeoutManager token={token} onLogout={logout} onTokenRefresh={handleTokenRefresh} />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
